import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMusic,
  faPlay,
  faChevronLeft,
  faGlobe,
  faGuitar,
  faUser,
  faListUl,
  faMinusCircle,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import QueueSourceListRow from './queue_source_list_row';

const server = axios.create({ baseURL: '/api' });

const KIND_META = {
  languages: { title: 'Languages', label: 'Language', icon: faGlobe, apiKind: 'languages', trackKind: 'language' },
  genres: { title: 'Genres', label: 'Genre', icon: faGuitar, apiKind: 'genres', trackKind: 'genre' },
  artists: { title: 'Artists', label: 'Artist', icon: faUser, apiKind: 'artists', trackKind: 'artist' },
};

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('walmates-token')}` };
}

function BrowseListView({
  items,
  kindMeta,
  activeBrowseKeys,
  appendMode = false,
  onOpen,
  onPlay,
}) {
  if (!items.length) {
    return (
      <div className="music-playlist-empty">
        <FontAwesomeIcon icon={kindMeta.icon} />
        <p>No {kindMeta.title.toLowerCase()} yet. Add tracks and run the catalog.</p>
      </div>
    );
  }

  return (
    <ul className="music-playlist-list">
      {items.map((item) => {
        const isActive = activeBrowseKeys?.has?.(item.key);
        const canPlay = (item.track_count || 0) > 0;
        return (
          <QueueSourceListRow
            key={item.key}
            coverUrl={item.cover_url || ''}
            fallbackIcon={kindMeta.icon}
            name={item.name}
            subtitle={`${item.track_count} track${item.track_count === 1 ? '' : 's'}${isActive ? ' · In queue' : ''}`}
            isActive={isActive}
            canPlay={canPlay}
            appendMode={appendMode}
            onPlay={() => onPlay?.(item)}
            onOpen={() => onOpen?.(item)}
          />
        );
      })}
    </ul>
  );
}

function BrowseDetailView({
  item,
  tracks,
  kindMeta,
  playTrack,
  loadingTrackId,
  isTrackSkipped,
  isActive,
  queueSource,
  onRemoveQueueSource,
  appendMode = false,
  onBack,
  onPlayAll,
  onPlayTrack,
  onAddTrack,
  onClose,
}) {
  return (
    <div className="music-playlist-detail">
      <div className="music-playlist-detail-nav">
        <button
          type="button"
          className="music-playlist-back"
          onClick={onBack}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
          <span>{kindMeta.title}</span>
        </button>
        <button type="button" className="music-playlist-sheet-done" onClick={onClose}>
          Done
        </button>
      </div>

      <div className="music-playlist-detail-header">
        <div className="music-playlist-detail-cover music-playlist-thumb-wrap">
          <div className="music-playlist-thumb music-playlist-detail-cover-inner">
            <div className="music-playlist-thumb-fallback">
              <FontAwesomeIcon icon={kindMeta.icon} />
            </div>
            {item.cover_url && <img src={item.cover_url} alt="" />}
          </div>
          {tracks.length > 0 && (
            <button
              type="button"
              className={[
                'music-playlist-thumb-action',
                appendMode ? 'is-add' : 'is-play',
              ].join(' ')}
              onClick={() => onPlayAll?.(item, tracks)}
              aria-label={appendMode ? `Add ${item.name} to queue` : `Play ${item.name}`}
            >
              <FontAwesomeIcon icon={appendMode ? faPlus : faPlay} />
            </button>
          )}
        </div>
        <div className="music-playlist-detail-info">
          <h2>{item.name}</h2>
          <p>
            {kindMeta.label}
            {' · '}
            {tracks.length} track{tracks.length === 1 ? '' : 's'}
            {isActive ? ' · In queue' : ''}
          </p>
        </div>
      </div>

      {(isActive && queueSource) && (
        <div className="music-playlist-detail-controls">
          <button
            type="button"
            className="music-playlist-control-btn"
            onClick={() => onRemoveQueueSource?.(queueSource.id)}
          >
            <FontAwesomeIcon icon={faMinusCircle} />
            <span>Remove from queue</span>
          </button>
        </div>
      )}

      {!tracks.length ? (
        <div className="music-playlist-empty">
          <FontAwesomeIcon icon={faMusic} />
          <p>No tracks in this {kindMeta.label.toLowerCase()}.</p>
        </div>
      ) : (
        <ul className="music-playlist-track-list">
          {tracks.map((track, index) => {
            const skipped = isTrackSkipped?.(track);
            const isLoading = loadingTrackId && (
              loadingTrackId === track.video_id || loadingTrackId === track.id
            );
            const isCurrent = playTrack
              && (playTrack.video_id === track.video_id || playTrack.id === track.id);
            const thumb = track.thumbnail_medium || track.thumbnail_default || '';
            return (
              <li
                key={track.video_id || index}
                className={[
                  'music-playlist-track-row',
                  skipped ? 'is-skipped' : '',
                  isCurrent ? 'is-current' : '',
                ].filter(Boolean).join(' ')}
              >
                <div className="music-playlist-track-main">
                  <span className="music-playlist-track-index" aria-hidden="true">
                    {isLoading ? '…' : index + 1}
                  </span>
                  <button
                    type="button"
                    className="music-playlist-track-art"
                    disabled={skipped}
                    onClick={() => {
                      if (skipped) return;
                      if (appendMode && onAddTrack) onAddTrack(track);
                      else onPlayTrack?.(item, tracks, track);
                    }}
                    aria-label={
                      skipped
                        ? `${track.title || 'Track'} (skipped)`
                        : (appendMode
                          ? `Add ${track.title || 'track'} to playing list`
                          : `Play ${track.title || 'track'}`)
                    }
                  >
                    {thumb ? <img src={thumb} alt="" /> : <FontAwesomeIcon icon={faMusic} />}
                    {!skipped && (
                      <span className={`music-playlist-track-play${appendMode ? ' is-add' : ''}`} aria-hidden="true">
                        <FontAwesomeIcon icon={appendMode ? faPlus : faPlay} />
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="music-playlist-track-text"
                    disabled={skipped}
                    onClick={() => {
                      if (!skipped) onPlayTrack?.(item, tracks, track);
                    }}
                    aria-label={
                      skipped
                        ? `${track.title || 'Track'} (skipped)`
                        : `Play ${track.title || 'track'} now`
                    }
                  >
                    <span className="music-playlist-track-title">
                      {track.title || 'Untitled'}
                    </span>
                    <span className="music-playlist-track-artist">
                      {track.artist || 'Unknown artist'}
                      {skipped ? ' · Skipped' : ''}
                    </span>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Browse Languages / Genres / Artists — playlist-like UI, not playlists.
 */
export default function CatalogBrowseModal({
  open,
  kind = 'languages',
  queueSources = [],
  appendMode = false,
  playTrack = null,
  loadingTrackId = null,
  isTrackSkipped,
  onPlayBrowse,
  onPlayTrackFromBrowse,
  onAddTrackToQueue,
  onRemoveQueueSource,
  onClose,
  initialKey = null,
}) {
  const kindMeta = KIND_META[kind] || KIND_META.languages;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailTracks, setDetailTracks] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const openedInitialRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setDetail(null);
      setDetailTracks([]);
      openedInitialRef.current = false;
      return;
    }
    let cancelled = false;
    setLoading(true);
    const user_id = localStorage.getItem('walmates-user_id');
    server
      .get(`/music/library/browse?user_id=${user_id}&kind=${kindMeta.apiKind}`, {
        headers: authHeaders(),
      })
      .then((res) => {
        if (!cancelled) setItems(res.data?.items || []);
      })
      .catch((err) => {
        console.error('Browse load failed:', err);
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, kindMeta.apiKind]);

  const openDetail = async (item) => {
    setDetail(item);
    setDetailLoading(true);
    setDetailTracks([]);
    const user_id = localStorage.getItem('walmates-user_id');
    try {
      const res = await server.get(
        `/music/library/browse/tracks?user_id=${user_id}&kind=${kindMeta.trackKind}&key=${encodeURIComponent(item.key)}`,
        { headers: authHeaders() }
      );
      setDetailTracks(res.data?.tracks || []);
    } catch (err) {
      console.error('Browse tracks failed:', err);
      setDetailTracks([]);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !initialKey || !items.length || openedInitialRef.current) return;
    const item = items.find((i) => i.key === initialKey);
    if (!item) return;
    openedInitialRef.current = true;
    openDetail(item);
  }, [open, initialKey, items]);

  const activeKeysForKind = useMemo(
    () => new Set(
      queueSources
        .filter((s) => s.kind === 'browse' && s.browseKind === kindMeta.trackKind)
        .map((s) => s.browseKey)
    ),
    [queueSources, kindMeta.trackKind]
  );

  const browseQueueSources = useMemo(
    () => queueSources.filter(
      (s) => s.kind === 'browse' && s.browseKind === kindMeta.trackKind
    ),
    [queueSources, kindMeta.trackKind]
  );

  const queueSourceForKey = (key) => browseQueueSources.find((s) => s.browseKey === key);

  if (!open) return null;

  const handleClose = () => {
    setDetail(null);
    setDetailTracks([]);
    onClose?.();
  };

  const handlePlayItem = async (item) => {
    const user_id = localStorage.getItem('walmates-user_id');
    try {
      const res = await server.get(
        `/music/library/browse/tracks?user_id=${user_id}&kind=${kindMeta.trackKind}&key=${encodeURIComponent(item.key)}`,
        { headers: authHeaders() }
      );
      const tracks = res.data?.tracks || [];
      onPlayBrowse?.({
        kind: kindMeta.trackKind,
        key: item.key,
        name: item.name,
        tracks,
      });
      if (!appendMode) handleClose();
    } catch (err) {
      console.error('Play browse failed:', err);
    }
  };

  return (
    <>
      <div
        className="music-playlist-backdrop"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        className={`music-playlist-sheet${detail ? ' music-playlist-sheet--detail' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={detail ? detail.name : kindMeta.title}
      >
        {detail ? (
          detailLoading ? (
            <div className="music-playlist-empty">
              <FontAwesomeIcon icon={faListUl} />
              <p>Loading tracks…</p>
            </div>
          ) : (
            <BrowseDetailView
              item={detail}
              tracks={detailTracks}
              kindMeta={kindMeta}
              playTrack={playTrack}
              loadingTrackId={loadingTrackId}
              isTrackSkipped={isTrackSkipped}
              isActive={activeKeysForKind.has(detail.key)}
              queueSource={queueSourceForKey(detail.key)}
              onRemoveQueueSource={onRemoveQueueSource}
              appendMode={appendMode}
              onBack={() => {
                setDetail(null);
                setDetailTracks([]);
              }}
              onPlayAll={(item, tracks) => {
                onPlayBrowse?.({
                  kind: kindMeta.trackKind,
                  key: item.key,
                  name: item.name,
                  tracks,
                });
                if (!appendMode) handleClose();
              }}
              onPlayTrack={(item, tracks, track) => {
                onPlayTrackFromBrowse?.({
                  kind: kindMeta.trackKind,
                  key: item.key,
                  name: item.name,
                  tracks,
                  startTrack: track,
                });
                if (!appendMode) handleClose();
              }}
              onAddTrack={onAddTrackToQueue}
              onClose={handleClose}
            />
          )
        ) : (
          <>
            <div className="music-playlist-sheet-header">
              <div>
                <h2>{kindMeta.title}</h2>
                <p>
                  {loading
                    ? 'Loading…'
                    : items.length === 0
                      ? `No ${kindMeta.title.toLowerCase()} yet`
                      : `${items.length} ${kindMeta.title.toLowerCase()}`}
                </p>
              </div>
              <button
                type="button"
                className="music-playlist-sheet-done"
                onClick={handleClose}
              >
                Done
              </button>
            </div>
            {loading ? (
              <div className="music-playlist-empty">
                <p>Loading…</p>
              </div>
            ) : (
              <BrowseListView
                items={items}
                kindMeta={kindMeta}
                activeBrowseKeys={activeKeysForKind}
                appendMode={appendMode}
                onOpen={openDetail}
                onPlay={handlePlayItem}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
